import { useState, useEffect } from "react";
import * as signalr from "@microsoft/signalr";
import { useAuth } from "react-oidc-context";

interface MessageData {
  [key: string]: unknown;
}

const isDev: boolean =
  import.meta.env.VITE_AUTH_CLIENT_ID?.toLowerCase().includes("dev") ?? false;

const mapMessages = (messages: string[]): MessageData => {
  const res: MessageData = {};
  for (const msg of messages) {
    res[msg] = null;
  }

  return res;
};

export function useSignalR(hubURL: string, messagesToSubscribe: string[] = []) {
  if (!hubURL) throw new Error("hubURL should now be empty");

  if (!Array.isArray(messagesToSubscribe) || messagesToSubscribe.length === 0)
    throw new Error("messagesToSubscribe should now be empty");

  const [connection, setConnection] = useState<signalr.HubConnection | null>(
    null
  );
  const [data, setMessageData] = useState<MessageData>(
    mapMessages(messagesToSubscribe)
  );

  const { user } = useAuth();
  const access_token = user?.access_token;

  useEffect(() => {
    let isMounted = true; // Track if component is mounted
    if (!access_token) return () => null;
    const connect = new signalr.HubConnectionBuilder()
      .withUrl(hubURL, {
        accessTokenFactory: () => `${access_token}`,
      })
      .configureLogging(isDev ? signalr.LogLevel.Debug : signalr.LogLevel.Error)
      .withAutomaticReconnect()
      .build();

    setConnection(connect);

    return () => {
      isMounted = false; // Component is unmounted
      if (connect.state === signalr.HubConnectionState.Connected) {
        connect
          .stop()
          .then(() => {
            if (isMounted) {
              // Check if component is still mounted
              setConnection(null);
            }
          })
          .catch((err: Error) => {
            console.error(err);
          });
      } else {
        if (isMounted) {
          // Check if component is still mounted
          setConnection(null);
        }
      }
    };
  }, [access_token, hubURL]);

  useEffect(() => {
    if (connection?.state !== signalr.HubConnectionState.Disconnected)
      return undefined;

    connection
      .start()
      .then(() => {
        for (const message of messagesToSubscribe) {
          connection.on(message, (notifications) => {
            setMessageData((prevData) => ({
              ...prevData,
              [message]: notifications,
            }));
          });
        }
      })
      .catch((err: Error) => {
        console.error(err);
      });

    return undefined;
  }, [connection, messagesToSubscribe]);

  return { data };
}
